<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<template #content>
			<!-- Figma 7415:17771: laid out as the receipts — the tile 104 from the top (24 + 80), the text 24 under it. -->
			<LayoutFlex padding="var(--size-24) var(--size-20)" gap="0" align-items="center">
				<LayoutFlex padding="var(--size-80) 0 0" gap="var(--size-24)" align-items="center">
					<div class="failed__icon" aria-hidden="true">
						<RoseCross />
					</div>
					<LayoutFlex padding="0" gap="var(--size-12)" align-items="center">
						<Text font="--heading-h3" color="--text-dark-primary" text-align="center">Payment unsuccessful</Text>
						<Text font="--text-16-22-reg" color="--text-dark-primary" text-align="center">
							We couldn’t process your payment, so your cancellation isn’t complete and your KOVA plan is still active.
						</Text>
						<Text font="--text-16-22-reg" color="--text-dark-primary" text-align="center">
							Please check that your payment method is active and has sufficient funds, then try again or update your
							payment method.
						</Text>
					</LayoutFlex>
				</LayoutFlex>
			</LayoutFlex>
		</template>

		<template #footer>
			<LayoutFlex padding="0 var(--size-20) var(--size-24)" gap="var(--size-16)" align-items="center">
				<Button full-width @ds:click="tryAgain">
					<Text font="--text-16-18-semi">Try again</Text>
				</Button>
				<!-- Figma's green-tinted secondary (7415:17784) has no DS Button variant: a native button. -->
				<button type="button" class="failed__update" @click="updatePaymentMethod">
					<Text font="--text-16-18-semi" color="--background-brand-high">Update payment method</Text>
				</button>
				<Text font="--text-16-22-reg" color="--text-dark-primary" text-align="center">
					Any questions?
					<a class="failed__support-link" :href="`mailto:${SUPPORT_EMAIL}`" @click="onContactSupport">
						Contact support
					</a>
				</Text>
			</LayoutFlex>
		</template>
	</LayoutScreen>
</template>

<script setup lang="ts">
// /cancel/commitment/failed — Figma `kova cancel flow1` 6455:21896, now 7415:17771: the exit-fee charge was declined
// (or, not expected, asked for 3DS). Nothing was cancelled — the plan is still active.
// Analytics: PAYMENT_UNSUCCESSFUL (AL-15098) — `try_again` / `update_payment_method`; "Contact support"
// is `web_cancellation_payment_unsuccessful`; the card change reports `cancellation_payment_unsuccessful_screen`.
// TODO(DEV-35727): "Try again" re-runs the charge from end-now; show the decline `reason_code`.
import { useRouter } from 'vue-router'
import { Button, LayoutFlex, LayoutScreen, Text } from '@simple-life-app/growth-ui-kova'
import RoseCross from '@/components/cancel/RoseCross.vue'
import { COMMITMENT_ROUTES } from '@/cancelFlow/commitment'
import { STEP_TITLE, trackSupportEntryPoint, useCancelStepLogging } from '@/analytics'
import { SUPPORT_EMAIL } from '@/config'

const router = useRouter()

// Why the payment failed (agreed with analytics 2026-10-21), as end-now handed it over in the history state:
// `card_declined` (+ billing's `reason_code`), `requires_3ds`, or `unknown` — a 500 / lost answer, and any
// arrival without the hand-over (a reload, a deep link).
const step = useCancelStepLogging(STEP_TITLE.PAYMENT_UNSUCCESSFUL, {
	getData: () => {
		const state = window.history.state ?? {}
		return {
			payment_error: state.payment_error ?? 'unknown',
			...(state.reason_code ? { reason_code: state.reason_code } : {}),
		}
	},
})

function tryAgain() {
	step.record(['try_again'])
	router.replace(COMMITMENT_ROUTES.endNow)
}

function updatePaymentMethod() {
	step.record(['update_payment_method'])
	router.push({ path: '/payment/update', state: { entry: 'cancellation_payment_unsuccessful_screen' } })
}

function onContactSupport() {
	trackSupportEntryPoint('web_cancellation_payment_unsuccessful')
}
</script>

<style scoped>
/* Figma "Icon": 64×64, radius 24, `status/error/bg` rose 10% (the DS rose tints are 8% / 20%). */
.failed__icon {
	width: var(--size-64);
	height: var(--size-64);
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(203, 49, 82, 0.1);
	border-radius: var(--size-24);
}

/* `status/success/bg` with `text/accent/green` #00523c: 48 tall, radius 20, as the Kova primary. */
.failed__update {
	appearance: none;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: var(--size-48);
	padding: var(--size-12);
	background: rgba(16, 122, 68, 0.1);
	border: none;
	border-radius: var(--size-20);
	cursor: pointer;
	transition: filter 0.15s ease;
}

.failed__update:hover {
	filter: brightness(0.97);
}

.failed__update:focus-visible {
	outline: 2px solid var(--stroke-accent-brand);
	outline-offset: 2px;
}

/* DS Text ships `.text a { color: inherit }` at (0,2,1): the `.text a` prefix lets the brand green win. */
.text a.failed__support-link {
	color: var(--background-brand-high);
	text-decoration: underline;
}
</style>
